/**
 * Цвета организации: акцент (коралл #EC7354) и основной (бирюза #1B7984).
 * Основной цвет — сайдбар, меню, выделенный текст и светлый фон карточек.
 */

import logger from './logger'

const ONLINE_APPEARANCE_KEY = 'onlineOrgAppearance'

export const DEFAULT_ACCENT_COLOR = '#EC7354'
export const DEFAULT_MAIN_COLOR = '#1B7984'

export const ACCENT_PRESETS = [
  { key: 'coral', hex: '#EC7354' },
  { key: 'blue', hex: '#2563EB' },
  { key: 'emerald', hex: '#059669' },
  { key: 'violet', hex: '#7C3AED' },
  { key: 'amber', hex: '#D97706' },
  { key: 'crimson', hex: '#E11D48' },
]

export const MAIN_PRESETS = [
  { key: 'teal', hex: '#1B7984' },
  { key: 'blue', hex: '#2563EB' },
  { key: 'navy', hex: '#0F172A' },
  { key: 'slate', hex: '#1E293B' },
  { key: 'emerald', hex: '#064E3B' },
  { key: 'brown', hex: '#292524' },
]

/**
 * Приводит цвет к #RRGGBB. Невалидное значение → null.
 * @param {string|null|undefined} value
 * @returns {string|null}
 */
export function normalizeAccentHex(value) {
  if (value == null) return null

  let hex = String(value).trim()
  if (!hex) return null
  if (hex[0] !== '#') hex = `#${hex}`

  if (/^#[0-9A-Fa-f]{3}$/.test(hex)) {
    hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
  }

  if (!/^#[0-9A-Fa-f]{6}$/.test(hex)) return null

  return hex.toUpperCase()
}

function hexToRgbChannels(hex) {
  const n = parseInt(hex.slice(1), 16)

  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`
}

function mixHex(hex, target, amount) {
  const from = parseInt(hex.slice(1), 16)
  const to = parseInt(target.slice(1), 16)
  const mix = (shift) => {
    const a = (from >> shift) & 255
    const b = (to >> shift) & 255

    return Math.round(a + (b - a) * amount)
  }

  const r = mix(16)
  const g = mix(8)
  const b = mix(0)

  return `#${[r, g, b]
    .map((channel) => channel.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`
}

/**
 * CSS-переменные акцента. Невалидный hex → {}.
 * @param {string|null|undefined} hex
 * @returns {Record<string, string>}
 */
export function accentCssVars(hex) {
  const color = normalizeAccentHex(hex)
  if (!color) return {}

  const hover = mixHex(color, '#FFFFFF', 0.12)
  const active = mixHex(color, '#FFFFFF', 0.3)

  return {
    '--main-accent-color': color,
    '--main-accent-rgb': hexToRgbChannels(color),
    '--main-button-color': color,
    '--main-button-color-hover': hover,
    '--main-button-color-active': active,
    '--Colors-Brand-Secondary-500': color,
    '--Colors-Brand-Secondary-600': hover,
    '--Colors-Brand-Secondary-700': mixHex(color, '#000000', 0.14),
    '--Colors-Brand-Secondary-Disabled': mixHex(color, '#FFFFFF', 0.64),
  }
}

function cabinetColorVars(org, applyKey, colorKey, toVars) {
  if (!org || org[applyKey] === false) return {}

  return toVars(org[colorKey])
}

/**
 * Переменные акцента ЛК. Флаг выкл / нет org → {}.
 * @param {{ accentApplyCabinet?: boolean, accentColor?: string }|null|undefined} org
 */
export function cabinetAccentVars(org) {
  return cabinetColorVars(
    org,
    'accentApplyCabinet',
    'accentColor',
    accentCssVars
  )
}

const ACCENT_CSS_VAR_NAMES = [
  '--main-accent-color',
  '--main-accent-rgb',
  '--main-button-color',
  '--main-button-color-hover',
  '--main-button-color-active',
  '--Colors-Brand-Secondary-500',
  '--Colors-Brand-Secondary-600',
  '--Colors-Brand-Secondary-700',
  '--Colors-Brand-Secondary-Disabled',
]

/**
 * CSS-переменные основного цвета. Невалидный hex → {}.
 * @param {string|null|undefined} hex
 * @returns {Record<string, string>}
 */
export function mainCssVars(hex) {
  const color = normalizeAccentHex(hex)
  if (!color) return {}

  const rgb = hexToRgbChannels(color)
  const surface = mixHex(color, '#FFFFFF', 0.92)

  return {
    '--main-color': color,
    '--main-color-rgb': rgb,
    '--main-active-color': mixHex(color, '#FFFFFF', 0.18),
    '--main-hover-bg-color': `rgba(${rgb}, 0.2)`,
    '--main-card-bg-color': surface,
    '--table-accent-color': surface,
    '--brand': `rgba(${rgb}, 0.12)`,
    '--Colors-Brand-Primary-500': color,
    '--Colors-Brand-Primary-600': mixHex(color, '#FFFFFF', 0.12),
    '--Colors-Brand-Primary-700': mixHex(color, '#000000', 0.18),
    '--Colors-Brand-Primary-Disabled': mixHex(color, '#FFFFFF', 0.82),
  }
}

/**
 * Переменные основного цвета ЛК. Флаг выкл / нет org → {}.
 * @param {{ mainApplyCabinet?: boolean, mainColor?: string }|null|undefined} org
 */
export function cabinetMainVars(org) {
  return cabinetColorVars(org, 'mainApplyCabinet', 'mainColor', mainCssVars)
}

export function cabinetAppearanceVars(org) {
  return {
    ...cabinetAccentVars(org),
    ...cabinetMainVars(org),
  }
}

export function onlineAppearanceVars(org) {
  if (!org) return {}

  return {
    ...accentCssVars(org.accentColor),
    ...mainCssVars(org.mainColor),
  }
}

export function readAppearance() {
  if (typeof sessionStorage === 'undefined') return null

  const raw = sessionStorage.getItem(ONLINE_APPEARANCE_KEY)
  if (!raw) return null

  try {
    const org = JSON.parse(raw)
    if (!org || typeof org !== 'object') return null

    return org
  } catch (error) {
    logger.error('Не удалось прочитать цвета онлайн-записи', error)
    return null
  }
}

export function saveAppearance(org) {
  if (typeof sessionStorage === 'undefined' || !org) return

  try {
    sessionStorage.setItem(
      ONLINE_APPEARANCE_KEY,
      JSON.stringify({
        id: org.id ?? null,
        name: org.name || '',
        logo: org.logo || null,
        accentColor: org.accentColor || null,
        mainColor: org.mainColor || null,
      })
    )
  } catch (error) {
    logger.error('Не удалось сохранить цвета онлайн-записи', error)
  }
}

const MAIN_CSS_VAR_NAMES = [
  '--main-color',
  '--main-color-rgb',
  '--main-active-color',
  '--main-hover-bg-color',
  '--main-card-bg-color',
  '--table-accent-color',
  '--brand',
  '--Colors-Brand-Primary-500',
  '--Colors-Brand-Primary-600',
  '--Colors-Brand-Primary-700',
  '--Colors-Brand-Primary-Disabled',
]

function applyCssVars(names, vars) {
  if (typeof document === 'undefined') return

  const root = document.documentElement
  const next = vars || {}

  names.forEach((name) => {
    const value = next[name]
    if (!value) {
      root.style.removeProperty(name)
      return
    }

    root.style.setProperty(name, value)
  })
}

/**
 * Пишет переменные акцента на <html>. Пустой next — сброс к :root.
 * @param {Record<string, string>|null|undefined} vars
 */
export function setDocumentAccentVars(vars) {
  applyCssVars(ACCENT_CSS_VAR_NAMES, vars)
}

/**
 * Пишет переменные основного цвета на <html>. Пустой next — сброс к :root.
 * @param {Record<string, string>|null|undefined} vars
 */
export function setDocumentMainVars(vars) {
  applyCssVars(MAIN_CSS_VAR_NAMES, vars)
}

/**
 * Пишет акцент и основной цвет на <html>.
 * @param {Record<string, string>|null|undefined} vars
 */
export function setDocumentAppearanceVars(vars) {
  setDocumentAccentVars(vars)
  setDocumentMainVars(vars)
}
